(() => {
  window.ACCatalog?.destroy?.();

  const cleanups = [];
  const facetControllers = new Map();
  const catalogueControllers = new Map();
  const compact = window.matchMedia('(max-width: 1023px)');
  const reducedMotion = () => document.body?.dataset.acMotion === 'reduced' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const on = (target, type, listener, options) => {
    target.addEventListener(type, listener, options);
    const cleanup = () => target.removeEventListener(type, listener, options);
    cleanups.push(cleanup);
    return cleanup;
  };
  const focusable = (root) => [...root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')].filter((element) => (
    !element.closest('[hidden], [inert]') && element.getClientRects().length > 0
    && (!element.closest('details:not([open])') || element.matches('details:not([open]) > summary'))
  ));

  class FacetsController {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-ac-facets-form]');
      this.panel = root.querySelector('[data-ac-facets-panel]');
      this.toggle = root.querySelector('[data-ac-facets-toggle]');
      this.close = root.querySelector('[data-ac-facets-close]');
      this.updateBehavior = root.dataset.acFacetsUpdate || 'apply';
      this.open = false;
      this.cleanups = [];
      this.focusFrame = 0;
      this.destroyed = false;
      if (!this.form || !this.panel || !this.toggle) return;
      this.nativePopover = this.panel.getAttribute('popover');
      this.nativeRole = this.panel.getAttribute('role');
      this.layerId = `facets-${this.panel.id}`;
      const wasOpen = this.nativePopover !== null && typeof this.panel.hidePopover === 'function' && this.panel.matches(':popover-open');
      const focused = this.panel.contains(document.activeElement) ? document.activeElement : null;
      if (wasOpen) this.panel.hidePopover();
      this.panel.removeAttribute('popover');
      this.toggle.removeAttribute('popovertarget');
      this.close?.removeAttribute('popovertarget');
      this.root.classList.add('is-enhanced');
      this.bind();
      this.syncMode(wasOpen, focused);
    }

    on(target, type, listener, options) {
      if (!target) return;
      target.addEventListener(type, listener, options);
      this.cleanups.push(() => target.removeEventListener(type, listener, options));
    }

    setOpen(open, restoreFocus = false, focusTarget = null) {
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = 0;
      this.open = compact.matches && open;
      this.root.classList.toggle('is-open', this.open);
      this.toggle.setAttribute('aria-expanded', String(this.open));
      this.panel.setAttribute('aria-hidden', String(compact.matches && !this.open));
      this.panel.inert = compact.matches && !this.open;
      this.panel.setAttribute('role', compact.matches ? 'dialog' : 'group');
      if (this.open) {
        this.panel.setAttribute('aria-modal', 'true');
        window.AcLayerManager?.acquire(this.layerId, {
          opener: this.toggle,
          getFocusRoots: () => [this.panel],
          getInertRoots: () => {
            const roots = [];
            for (let branch = this.panel; branch.parentElement && branch !== document.body; branch = branch.parentElement) {
              roots.push(...[...branch.parentElement.children].filter((node) => node !== branch && !node.matches('script, style, link, template')));
            }
            return roots;
          },
          onEscape: () => this.setOpen(false, true),
        });
      } else {
        this.panel.removeAttribute('aria-modal');
        window.AcLayerManager?.release(this.layerId, { restoreFocus });
      }
      document.body.classList.toggle('ac-facets-open', Boolean(document.querySelector('[data-ac-facets].is-open')));
      if (!this.destroyed && (this.open || restoreFocus)) {
        this.focusFrame = requestAnimationFrame(() => {
          this.focusFrame = 0;
          if (this.destroyed || !this.root.isConnected) return;
          const target = this.open ? (focusTarget?.isConnected ? focusTarget : focusable(this.panel)[0]) : this.toggle;
          target?.focus({ preventScroll: true });
        });
      }
    }

    syncMode(open = false, focusTarget = null) {
      this.setOpen(open, false, focusTarget);
      if (!compact.matches) {
        this.panel.removeAttribute('aria-hidden');
        this.panel.inert = false;
      }
    }

    bind() {
      this.on(this.toggle, 'click', () => this.setOpen(!this.open));
      this.on(this.close, 'click', () => this.setOpen(false, true));
      this.on(this.root, 'change', (event) => {
        if (!(event.target instanceof HTMLInputElement)) return;
        if (event.target.hasAttribute('data-ac-facets-auto-submit')) {
          this.form.requestSubmit();
          return;
        }
        if (this.updateBehavior === 'instant') this.form.requestSubmit();
      });
      this.on(this.panel, 'keydown', (event) => {
        if (event.key === 'Escape' && this.open) {
          event.preventDefault();
          this.setOpen(false, true);
          return;
        }
        if (event.key !== 'Tab' || !this.open) return;
        if (window.AcLayerManager) return;
        const controls = focusable(this.panel);
        if (!controls.length) return;
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      this.on(compact, 'change', () => this.syncMode());
    }

    destroy() {
      const wasOpen = this.open;
      const focused = this.panel.contains(document.activeElement) ? document.activeElement : null;
      this.destroyed = true;
      this.cleanups.splice(0).reverse().forEach((cleanup) => cleanup());
      this.setOpen(false);
      this.panel.removeAttribute('aria-hidden');
      this.panel.inert = false;
      this.panel.removeAttribute('aria-modal');
      if (this.nativeRole === null) this.panel.removeAttribute('role');
      else this.panel.setAttribute('role', this.nativeRole);
      this.toggle.removeAttribute('aria-expanded');
      this.root.classList.remove('is-enhanced');
      if (this.nativePopover !== null) {
        this.panel.setAttribute('popover', this.nativePopover);
        this.toggle.setAttribute('popovertarget', this.panel.id);
        this.close?.setAttribute('popovertarget', this.panel.id);
        if (wasOpen && this.root.isConnected && this.root.getClientRects().length && typeof this.panel.showPopover === 'function') {
          this.panel.showPopover();
          focused?.focus({ preventScroll: true });
        }
      }
    }
  }

  class CatalogHeader {
    constructor(root) {
      this.root = root;
      this.controls = [...root.querySelectorAll('.ac-search-form, .ac-page-header__primary, .ac-page-header__count, .ac-catalog__toolbar')].map((node) => {
        const placeholder = document.createElement('div');
        placeholder.setAttribute('aria-hidden', 'true');
        placeholder.dataset.acCatalogHeaderPlaceholder = '';
        node.before(placeholder);
        return { node, placeholder, inert: node.inert };
      });
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'ac-catalog__header-backdrop';
      this.backdrop.setAttribute('aria-hidden', 'true');
      this.pagination = root.querySelector('.ac-catalog__pagination');
      root.append(this.backdrop);
      this.update = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.paint(); });
      };
      this.resize = () => {
        if (!this.resizeFrame) this.resizeFrame = requestAnimationFrame(() => { this.resizeFrame = 0; this.measure(); });
      };
      this.measure();
      window.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.resize, { passive: true });
      document.fonts?.addEventListener('loadingdone', this.resize);
      this.observer = new ResizeObserver(this.resize);
      this.observer.observe(root);
      if (this.pagination) this.observer.observe(this.pagination);
      this.controls.forEach(({ node }) => this.observer.observe(node));
    }

    measure() {
      const rootTop = this.root.getBoundingClientRect().top;
      // Fixed widths cannot reveal a font or breakpoint's new intrinsic size.
      this.controls.forEach(({ node, placeholder }) => {
        node.classList.remove('ac-catalog__pinned-control');
        placeholder.style.display = 'none';
      });
      const measured = this.controls.map(({ node, placeholder }) => {
        const style = getComputedStyle(node);
        return { node, placeholder, rect: node.getBoundingClientRect(),
          style: Object.fromEntries(['gridColumn', 'gridRow', 'justifySelf', 'alignSelf', 'margin', 'marginLeft', 'marginTop'].map((key) => [key, style[key]])) };
      });
      measured.forEach(({ node, placeholder, rect, style }) => {
        ['gridColumn', 'gridRow', 'justifySelf', 'alignSelf', 'margin'].forEach((key) => { placeholder.style[key] = style[key]; });
        placeholder.style.blockSize = `${rect.height}px`;
        placeholder.style.inlineSize = style.justifySelf === 'start' ? `${rect.width}px` : '';
        placeholder.style.removeProperty('display');
        node.style.setProperty('--ac-catalog-pin-x', `${rect.left - (parseFloat(style.marginLeft) || 0)}px`);
        node.style.setProperty('--ac-catalog-pin-y', `${rect.top - rootTop - (parseFloat(style.marginTop) || 0)}px`);
        node.style.setProperty('--ac-catalog-pin-width', `${rect.width}px`);
        node.classList.add('ac-catalog__pinned-control');
      });
      this.threshold = Math.max(0, ...this.controls.map(({ node }) => parseFloat(node.style.getPropertyValue('--ac-catalog-pin-y')) + node.offsetHeight));
      this.backdrop.style.blockSize = `${this.threshold + 16}px`;
      const rootRect = this.root.getBoundingClientRect();
      const pageY = window.scrollY;
      this.geometry = {
        top: rootRect.top + pageY,
        bottom: rootRect.bottom + pageY,
        paginationTop: this.pagination ? this.pagination.getBoundingClientRect().top + pageY : Infinity,
      };
      this.paint();
    }

    paint() {
      const threshold = this.threshold;
      const lenis = window.AcSmoothScroll?.lenis;
      const scroll = lenis && !lenis.isStopped ? lenis.animatedScroll : NaN;
      let rootTop;
      let rootBottom;
      let paginationTop;
      if (this.geometry && Number.isFinite(scroll)) {
        rootTop = this.geometry.top - scroll;
        rootBottom = this.geometry.bottom - scroll;
        paginationTop = this.geometry.paginationTop - scroll;
      } else {
        const rect = this.root.getBoundingClientRect();
        rootTop = rect.top;
        rootBottom = rect.bottom;
        paginationTop = this.pagination?.getBoundingClientRect().top;
      }
      const hidden = rootTop > 1 || rootBottom < threshold + 32 || paginationTop < threshold + 32;
      const backdropHidden = hidden || this.controls.length === 0 || this.overlay;
      if (this.backdrop.hidden !== Boolean(backdropHidden)) this.backdrop.hidden = Boolean(backdropHidden);
      if (hidden === this.hidden) return;
      this.hidden = hidden;
      this.controls.forEach(({ node, inert }) => {
        node.classList.toggle('ac-catalog__pinned-control--hidden', hidden);
        node.inert = hidden || inert;
      });
    }

    destroy() {
      window.removeEventListener('scroll', this.update);
      window.removeEventListener('resize', this.resize);
      document.fonts?.removeEventListener('loadingdone', this.resize);
      this.observer.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.resizeFrame);
      this.backdrop.remove();
      this.controls.forEach(({ node, placeholder, inert }) => {
        node.classList.remove('ac-catalog__pinned-control', 'ac-catalog__pinned-control--hidden');
        ['--ac-catalog-pin-x', '--ac-catalog-pin-y', '--ac-catalog-pin-width'].forEach((property) => node.style.removeProperty(property));
        node.inert = inert;
        placeholder.remove();
      });
    }
  }

  class EditorialScroll {
    constructor(list) {
      this.list = list;
      this.viewport = list.querySelector('[data-ac-product-list-viewport]');
      this.track = list.querySelector('[data-ac-product-list-track]');
      this.items = [...(this.track?.children || [])];
      this.frame = 0;
      this.resizeFrame = 0;
      this.entries = [];
      this.distance = 0;
      this.progress = null;
      this.split = Math.ceil(this.items.length / 2);
      this.update = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.paint(); });
      };
      this.resize = () => {
        if (!this.resizeFrame) this.resizeFrame = requestAnimationFrame(() => { this.resizeFrame = 0; this.measure(); });
      };
      this.focus = (event) => {
        cancelAnimationFrame(this.focusFrame);
        this.focusFrame = requestAnimationFrame(() => this.revealFocus(event.target));
      };
      this.trackRole = this.track?.getAttribute('role') ?? null;
      if (!this.viewport || this.items.length < 2) return;
      const focused = this.list.contains(document.activeElement) ? document.activeElement : null;
      this.track.setAttribute('role', 'presentation');
      this.columns = [0, 1].map((column) => {
        const wrapper = document.createElement('li');
        wrapper.className = 'ac-editorial-scroll__column';
        wrapper.setAttribute('role', 'presentation');
        wrapper.dataset.acEditorialColumn = column === 0 ? 'left' : 'right';
        const items = document.createElement('ul');
        items.className = 'ac-editorial-scroll__items';
        items.setAttribute('role', 'list');
        wrapper.append(items);
        this.track.append(wrapper);
        return { wrapper, items };
      });
      this.list.classList.add('ac-editorial-scroll');
      this.items.forEach((item, index) => {
        item.dataset.acEditorialColumn = index < this.split ? 'left' : 'right';
        this.columns[index < this.split ? 0 : 1].items.append(item);
      });
      this.measure();
      window.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.resize, { passive: true });
      this.list.addEventListener('focusin', this.focus);
      this.observer = new ResizeObserver(this.resize);
      this.items.forEach((item) => this.observer.observe(item));
      focused?.focus({ preventScroll: true });
    }

    measure() {
      const gap = parseFloat(getComputedStyle(this.track).rowGap) || 24;
      const heights = this.items.map((item) => item.getBoundingClientRect().height);
      const totals = [0, 0];
      this.entries = this.items.map((item, index) => {
        const column = index < this.split ? 0 : 1;
        const entry = { item, column, offset: totals[column], height: heights[index] };
        totals[column] += heights[index] + gap;
        return entry;
      });
      this.height = window.innerHeight;
      this.travel = totals.map((total) => Math.max(0, total - gap - this.height));
      this.distance = Math.max(...this.travel, 1);
      this.list.style.setProperty('--ac-editorial-span', `${this.distance + this.height}px`);
      window.AcSmoothScroll?.lenis?.resize?.();
      this.progress = null;
      this.paint();
    }

    paint() {
      if (!this.entries.length) return;
      const progress = Math.max(0, Math.min(1, -this.list.getBoundingClientRect().top / this.distance));
      if (progress === this.progress) return;
      const moved = this.progress !== null;
      this.progress = progress;
      this.columns.forEach(({ wrapper }, column) => {
        const shift = column === 0 ? (progress - 1) * this.travel[0] : -progress * this.travel[1];
        wrapper.style.transform = `translateY(${shift}px)`;
      });
      if (moved) {
        if (!this.list.hasAttribute('data-ac-product-list-moving')) {
          this.list.setAttribute('data-ac-product-list-moving', '');
          this.list.dispatchEvent(new CustomEvent('ac:product-list-motion-start', { bubbles: true }));
        }
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 140);
      }
    }

    settle() {
      if (!this.list.hasAttribute('data-ac-product-list-moving')) return;
      this.list.removeAttribute('data-ac-product-list-moving');
      this.list.dispatchEvent(new CustomEvent('ac:product-list-motion-end', { bubbles: true }));
    }

    revealFocus(target) {
      const item = target.closest?.('[data-ac-editorial-column]');
      const entry = this.entries.find((candidate) => candidate.item === item);
      if (!entry) return;
      const bounds = target.getBoundingClientRect();
      const viewport = this.viewport.getBoundingClientRect();
      if (bounds.top >= viewport.top && bounds.bottom <= viewport.bottom) return;
      const targetOffset = entry.offset + bounds.top - item.getBoundingClientRect().top + bounds.height / 2;
      const travel = this.travel[entry.column];
      if (!travel) return;
      const progress = entry.column === 0
        ? (this.height / 2 - targetOffset + travel) / travel
        : (targetOffset - this.height / 2) / travel;
      const position = window.scrollY + this.list.getBoundingClientRect().top + Math.max(0, Math.min(1, progress)) * this.distance;
      const smoothScroll = window.AcSmoothScroll?.lenis;
      if (smoothScroll) smoothScroll.scrollTo(position, { immediate: true });
      else window.scrollTo({ top: position, behavior: 'instant' });
      this.paint();
    }

    destroy() {
      const focused = this.list.contains(document.activeElement) ? document.activeElement : null;
      window.removeEventListener('scroll', this.update);
      window.removeEventListener('resize', this.resize);
      this.list.removeEventListener('focusin', this.focus);
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.resizeFrame);
      cancelAnimationFrame(this.focusFrame);
      clearTimeout(this.settleTimer);
      this.settle();
      this.track.append(...this.items);
      this.columns?.forEach(({ wrapper }) => wrapper.remove());
      if (this.trackRole === null) this.track.removeAttribute('role');
      else this.track.setAttribute('role', this.trackRole);
      this.list.classList.remove('ac-editorial-scroll');
      this.list.style.removeProperty('--ac-editorial-span');
      this.items.forEach((item) => {
        delete item.dataset.acEditorialColumn;
      });
      focused?.focus({ preventScroll: true });
    }
  }

  class CatalogueController {
    constructor(root) {
      this.root = root;
      // Collection controls scroll with the page; only search retains its pinned header.
      this.header = root.matches('.ac-catalog--search') ? new CatalogHeader(root) : null;
      this.controls = [...root.querySelectorAll('[data-ac-catalog-view-control]')];
      this.list = root.querySelector('[data-ac-product-list]');
      this.cleanups = [];
      this.animations = new Set();
      this.imageSizes = new Map([...this.list?.querySelectorAll('.ac-product-card__media img[srcset]') || []].map((image) => [image, image.getAttribute('sizes')]));
      if (!this.list) return;
      this.root.classList.add('is-enhanced');
      const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      const syncMotion = () => {
        if (reducedMotion()) {
          const pending = this.pendingView;
          this.cancelAnimations();
          if (pending) this.commitView(pending);
        }
        this.syncEditorial();
      };
      motionPreference.addEventListener('change', syncMotion);
      this.cleanups.push(() => motionPreference.removeEventListener('change', syncMotion));
      const motionObserver = new MutationObserver(syncMotion);
      motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
      this.cleanups.push(() => motionObserver.disconnect());
      compact.addEventListener('change', syncMotion);
      this.cleanups.push(() => compact.removeEventListener('change', syncMotion));
      this.controls.forEach((control) => {
        const listener = () => this.apply(control.dataset.acCatalogViewControl, true);
        control.addEventListener('click', listener);
        this.cleanups.push(() => control.removeEventListener('click', listener));
      });
      this.apply(root.dataset.acCatalogView || 'standard');
    }

    apply(view, animate = false) {
      this.cancelAnimations();
      const accepted = ['standard', 'editorial', 'dense'];
      const next = accepted.includes(view) ? view : 'standard';
      this.pendingView = next;
      const generation = this.generation;
      if (animate && !reducedMotion()) {
        const exits = this.visibleCards().map((card) => this.animateCard(card,
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 400, easing: 'cubic-bezier(.25,.46,.45,.94)', fill: 'forwards' },
        ));
        Promise.all(exits.map((animation) => animation.finished)).then(() => {
          if (generation !== this.generation) return;
          this.cancelAnimations();
          this.commitView(next, true);
        }, () => {});
      } else this.commitView(next);
    }

    commitView(next, animate = false) {
      this.pendingView = null;
      const changed = this.root.dataset.acCatalogView !== next;
      const catalogTop = window.scrollY + this.root.getBoundingClientRect().top;
      this.root.dataset.acCatalogView = next;
      this.list.dataset.acProductListView = next;
      const sizes = this.root.getAttribute(`data-ac-image-sizes-${next}`);
      if (sizes) this.imageSizes.forEach((_, image) => {
        image.setAttribute('sizes', image.loading === 'lazy' ? `auto, ${sizes}` : sizes);
      });
      this.syncEditorial();
      if (changed) {
        // The moving columns fill the viewport beneath the transparent controls.
        const scrollTop = this.editorial ? window.scrollY + this.list.getBoundingClientRect().top : catalogTop;
        const smoothScroll = window.AcSmoothScroll?.lenis;
        smoothScroll?.resize?.();
        if (smoothScroll) smoothScroll.scrollTo(scrollTop, { immediate: true });
        else window.scrollTo({ top: scrollTop, behavior: 'instant' });
      }
      this.controls.forEach((control) => {
        const active = control.dataset.acCatalogViewControl === next;
        control.classList.toggle('is-active', active);
        control.setAttribute('aria-pressed', String(active));
      });
      if (animate && !reducedMotion()) {
        this.visibleCards().forEach((card, index) => {
          const direction = this.editorial && card.closest('[data-ac-editorial-column="left"]') ? '-50%' : '50%';
          this.animateCard(card,
            [{ opacity: 0, transform: `translateY(${direction})` }, { opacity: 1, transform: 'none' }],
            { duration: 600, delay: this.editorial ? 0 : index * 50, easing: 'cubic-bezier(.215,.61,.355,1)', fill: 'backwards' },
          );
        });
      }
    }

    visibleCards() {
      return [...this.list.querySelectorAll('.ac-product-card')].filter((card) => {
        const bounds = card.getBoundingClientRect();
        return bounds.bottom > 0 && bounds.top < window.innerHeight;
      });
    }

    animateCard(card, keyframes, timing) {
      const animation = card.animate(keyframes, timing);
      this.animations.add(animation);
      animation.finished.then(
        () => { if (timing.fill !== 'forwards') this.animations.delete(animation); },
        () => this.animations.delete(animation),
      );
      return animation;
    }

    cancelAnimations() {
      this.generation = (this.generation || 0) + 1;
      this.pendingView = null;
      this.animations.forEach((animation) => animation.cancel());
      this.animations.clear();
    }

    syncEditorial() {
      const enabled = this.root.dataset.acCatalogView === 'editorial' && !compact.matches && !reducedMotion();
      if (enabled && !this.editorial) this.editorial = new EditorialScroll(this.list);
      else if (!enabled && this.editorial) {
        this.editorial.destroy();
        this.editorial = null;
      }
      if (this.header) {
        this.header.overlay = Boolean(this.editorial);
        this.header.paint();
      }
    }

    destroy() {
      this.cancelAnimations();
      this.header?.destroy();
      this.editorial?.destroy();
      this.editorial = null;
      this.cleanups.splice(0).reverse().forEach((cleanup) => cleanup());
      this.imageSizes.forEach((sizes, image) => {
        if (sizes === null) image.removeAttribute('sizes');
        else image.setAttribute('sizes', sizes);
      });
      this.root.classList.remove('is-enhanced');
    }
  }

  const init = (scope = document) => {
    const facetRoots = [...scope.querySelectorAll('[data-ac-facets]')];
    const catalogues = [...scope.querySelectorAll('[data-ac-catalog]')];
    if (scope instanceof Element && scope.matches('[data-ac-facets]')) facetRoots.unshift(scope);
    if (scope instanceof Element && scope.matches('[data-ac-catalog]')) catalogues.unshift(scope);
    facetRoots.forEach((root) => {
      if (!facetControllers.has(root)) facetControllers.set(root, new FacetsController(root));
    });
    catalogues.forEach((root) => {
      if (!catalogueControllers.has(root)) catalogueControllers.set(root, new CatalogueController(root));
    });
  };
  const destroyWithin = (scope = document) => {
    [facetControllers, catalogueControllers].forEach((controllers) => {
      [...controllers.entries()].forEach(([root, controller]) => {
        if (scope === document || scope === root || scope.contains(root)) {
          controller.destroy();
          controllers.delete(root);
        }
      });
    });
  };
  const destroy = () => {
    destroyWithin(document);
    cleanups.splice(0).reverse().forEach((cleanup) => cleanup());
  };

  if (document.readyState === 'loading') on(document, 'DOMContentLoaded', () => init(), { once: true });
  else init();
  on(document, 'shopify:section:load', (event) => init(event.target));
  on(document, 'shopify:section:unload', (event) => destroyWithin(event.target));
  on(window, 'pagehide', (event) => {
    if (!event.persisted) destroy();
  });
  window.ACCatalog = { destroy, init };
})();
